"use client";
import React from "react";
import PropTypes from "prop-types";
import CustomImage from "../../atoms/customImage";
import PDPKeyHeighLights from "../pdpKeyHeighLights";
import { shallowEqual, useSelector } from "react-redux";
import isEmpty from "lodash.isempty";

/**
 * ImageZoom component that displays an image with optional zoom functionality and click handler.
 *
 * @date 17/07/2024
 * @param {Object} param - The component props.
 * @param {string} param.imageSrc - The source URL of the image to be displayed.
 * @param {Function} [param.onImageClick] - Optional callback function triggered when the image is clicked.
 * @param {string} [param.skuCode] - Optional SKU code related to the image.
 * @returns {JSX.Element} The rendered ImageZoom component.
 */
const ImageZoom = ({ imageSrc, onImageClick, disclaimerText, productTitle }) => {
  const { keyHighlightDetails = {} } = useSelector(
    (state) => ({
      keyHighlightDetails: state?.pdp?.clientCurrentClickedItem?.keyHighlightDetails,
    }),
    shallowEqual,
  );
  return (
    <div className="relative flex" id="imageZoomContainer">
      <div>
        <div
          id="original-image-wrapper"
          role="presentation"
          onClick={onImageClick}
          className="relative aspect-square w-full max-w-[800px] overflow-hidden md:rounded-16"
        >
          {!isEmpty(keyHighlightDetails) && <PDPKeyHeighLights keyHighlightDetails={keyHighlightDetails} />}
          <CustomImage
            id="original-image"
            src={imageSrc}
            width={800}
            height={800}
            isPriority={true}
            alt={productTitle}
            sizes="(max-width: 768px) 100vw, 50vw"
            itemProp="image"
            className="h-full w-full cursor-zoom-in rounded-[16px] object-cover"
          />
          <div className="absolute bottom-8 right-8 cursor-zoom-in rounded-8 bg-[#ffffffa3] p-4">
            <CustomImage src="/icons/arrows-out.svg" width={20} height={20} alt="" sizes="10vw" isPriority={false} />
          </div>
        </div>
        {disclaimerText && (
          <div className="-z-[1] -mt-14 flex items-center gap-8 rounded-b-[12px] bg-fnp-50 px-12 pb-8 pt-22">
            <p className="text-12 font-500 leading-[14px] text-grey-500 opacity-75 md:leading-16">{disclaimerText}</p>
          </div>
        )}
      </div>
    </div>
  );
};

ImageZoom.propTypes = {
  imageSrc: PropTypes.string.isRequired,
  onImageClick: PropTypes.func,
  disclaimerText: PropTypes.string,
  productTitle: PropTypes.string,
};

export default React.memo(ImageZoom);
